import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import { useSync } from "@/context/SyncContext";
import { useTier } from "@/context/TierContext";
import { getDeviceLocationSnapshot } from "@/lib/deviceLocation";
import { checkImageQuality } from "@/lib/qualityCheck";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Siren, Camera, Check, Loader2, ChevronLeft, DoorOpen, Link2, Wrench, X, ImagePlus, Video,
} from "lucide-react";
import { toast } from "sonner";

// Config-driven presets — static, observational shot plans only.
// No diagnosis, cause, severity, safety, habitability, code, or legal wording.
export const URGENT_PRESETS = [
  {
    key: "active_leak", label: "Active leak",
    shots: [
      "Wide view of the affected area",
      "Close-up of where the water appears to come from",
      "Any water on the floor, walls, or ceiling",
      "Belongings or fixtures in the area",
    ],
  },
  {
    key: "no_hot_water", label: "No hot water",
    shots: [
      "The tap or shower running",
      "The water heater unit",
      "Any dials, display, or reset button on the unit",
      "The area around the unit",
    ],
  },
  {
    key: "hvac_problem", label: "Heating or cooling problem",
    shots: [
      "The thermostat and its current reading",
      "The heating or cooling unit or vent",
      "Any labels or model / serial plate",
      "The affected room",
    ],
  },
  {
    key: "pest_sighting", label: "Pest sighting",
    shots: [
      "Wide view of where you saw it",
      "Close-up of what you observed",
      "Any droppings, nests, or possible entry points",
      "The surrounding area",
    ],
  },
  {
    key: "water_staining", label: "Water staining",
    shots: [
      "Wide view of the stained area",
      "Close-up of the discoloration",
      "The surface around it (ceiling, wall, or floor)",
      "Something for scale, if helpful",
    ],
  },
  {
    key: "appliance_failure", label: "Appliance failure",
    shots: [
      "The appliance from the front",
      "Any error message or display",
      "The model / serial label",
      "How it is connected (plug, hose, or vent)",
    ],
  },
  {
    key: "electrical_concern", label: "Electrical concern",
    shots: [
      "Wide view of the outlet, switch, or fixture",
      "Close-up of the item",
      "The breaker panel, if you can reach it safely",
      "The surrounding area",
    ],
  },
  {
    key: "access_problem", label: "Door, lock, or access-device problem",
    shots: [
      "The door, lock, or access device",
      "Close-up of the part that isn't working",
      "How it fails to operate, if visible",
      "The surrounding frame or hardware",
    ],
  },
];

const uid = () => window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;

export function UrgentConditionCapture({ property, rooms = [], initialRoomId = null, open, onOpenChange, onLinked }) {
  const navigate = useNavigate();
  const { captureAndSync } = useSync();
  const { isPremium, openUpgrade } = useTier();

  const propertyId = property?.property_id;
  const storeKey = `dd_urgent_${propertyId}`;
  const readOnly = property?.archive_status === "archived" || !!property?.is_locked || !!property?.surrender;

  const [step, setStep] = useState("preset");
  const [roomId, setRoomId] = useState(initialRoomId || (rooms.length === 1 ? rooms[0].room_id : ""));
  const [presetKey, setPresetKey] = useState("");
  const [captured, setCaptured] = useState([]); // [{upload_id, photo_id, thumb, flagged}]
  const [busy, setBusy] = useState(false);
  const [issues, setIssues] = useState(null); // for "link to existing" picker
  const [loadingIssues, setLoadingIssues] = useState(false);

  const fileRef = useRef(null);

  const preset = useMemo(() => URGENT_PRESETS.find((p) => p.key === presetKey) || null, [presetKey]);
  const roomObj = rooms.find((r) => r.room_id === roomId);

  // Restore a prior in-progress session (survives reload / lost-response recovery).
  useEffect(() => {
    if (!open || !propertyId) return;
    try {
      const raw = localStorage.getItem(storeKey);
      if (raw) {
        const s = JSON.parse(raw);
        if (s.presetKey) setPresetKey(s.presetKey);
        if (s.roomId && rooms.some((r) => r.room_id === s.roomId)) setRoomId(s.roomId);
        if (Array.isArray(s.captured) && s.captured.length) {
          setCaptured(s.captured);
          setStep("capture");
          return;
        }
        if (s.presetKey) { setStep(s.roomId ? "capture" : "room"); }
      }
    } catch { /* ignore corrupt session */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, propertyId]);

  const persist = useCallback((next) => {
    try {
      localStorage.setItem(storeKey, JSON.stringify({
        presetKey: next.presetKey ?? presetKey,
        roomId: next.roomId ?? roomId,
        captured: (next.captured ?? captured).map((c) => ({ upload_id: c.upload_id, photo_id: c.photo_id, storage_path: c.storage_path, flagged: c.flagged })),
        updated_at: new Date().toISOString(),
      }));
    } catch { /* storage full — non-fatal */ }
  }, [storeKey, presetKey, roomId, captured]);

  const resetSession = useCallback(() => {
    try { localStorage.removeItem(storeKey); } catch { /* noop */ }
    setCaptured([]);
    setPresetKey("");
    setRoomId(initialRoomId || (rooms.length === 1 ? rooms[0].room_id : ""));
    setIssues(null);
    setStep("preset");
  }, [storeKey, initialRoomId, rooms]);

  const choosePreset = (key) => {
    setPresetKey(key);
    persist({ presetKey: key });
    setStep(roomId ? "capture" : "room");
  };

  const chooseRoom = (rid) => {
    setRoomId(rid);
    persist({ roomId: rid });
    setStep("capture");
  };

  // Capture a photo through the EXACT existing resilient queue (free, ordinary TenantSeal photo).
  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length || !roomId) return;
    setBusy(true);
    const dl = await getDeviceLocationSnapshot();
    for (const file of files) {
      let flags = [];
      try { flags = await checkImageQuality(file); } catch { flags = []; }
      const uploadId = uid();
      const capturedAt = new Date().toISOString();
      const rec = {
        client_upload_id: uploadId,
        property_id: propertyId,
        room_id: roomId,
        room_label: roomObj?.name || "Room",
        phase: property?.doc_type || "move_in",
        image_blob: file,
        content_type: file.type || "image/jpeg",
        client_captured_at: capturedAt,
        import_mode: property?.import_mode || "live",
        quality_flags: flags.length ? JSON.stringify({ flags, kept_despite_warning: true }) : null,
        device_location: dl || null,
      };
      try {
        const res = await captureAndSync(rec);
        const item = {
          upload_id: uploadId,
          photo_id: res.photo?.photo_id || null,
          storage_path: res.photo?.storage_path || null,
          thumb: res.photo?.storage_path ? fileUrl(res.photo.storage_path, 400) : URL.createObjectURL(file),
          flagged: flags.length > 0,
        };
        setCaptured((cur) => { const next = [...cur, item]; persist({ captured: next }); return next; });
        if (!res.photo && res.persisted) toast("Saved on this device — syncs when you\u2019re back online");
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Couldn't save that photo.");
      }
    }
    setBusy(false);
  };

  // Keep the evidence as ordinary room photos — nothing linked, nothing reported. (Free)
  const keepUnlinked = () => {
    toast.success(`Saved to ${roomObj?.name || "your room"} — kept unlinked.`);
    resetSession();
    onOpenChange(false);
    onLinked?.();
  };

  const startLinkExisting = async () => {
    if (!isPremium) { openUpgrade("maintenance"); return; }
    setLoadingIssues(true);
    try {
      const { data } = await api.get(`/properties/${propertyId}/maintenance`);
      setIssues(data || []);
      setStep("link");
    } catch (err) {
      if (err?.response?.status === 402) { openUpgrade("maintenance"); }
      else toast.error(err?.response?.data?.detail || "Couldn't load your repair cases.");
    } finally { setLoadingIssues(false); }
  };

  const successfulIds = () => captured.filter((c) => c.photo_id).map((c) => c.photo_id);
  const pendingCount = () => captured.filter((c) => !c.photo_id).length;

  const linkGuard = () => {
    const ids = successfulIds();
    if (ids.length) return ids;
    if (pendingCount() > 0) toast.error("Your photos are still uploading — wait a moment, then try again.");
    else toast.error("No uploaded photos to link yet.");
    return null;
  };

  const linkToIssue = async (issue) => {
    const ids = linkGuard();
    if (!ids) return;
    setBusy(true);
    try {
      // Reference the EXACT successfully-uploaded photo ids on the owned issue — no re-upload, no duplicate.
      await api.post(`/maintenance/${issue.issue_id}/link-photos`, { photo_ids: ids });
      toast.success(`Linked ${ids.length} photo${ids.length > 1 ? "s" : ""} to “${issue.title}”.`);
      resetSession();
      onOpenChange(false);
      onLinked?.();
    } catch (err) {
      if (err?.response?.status === 402) openUpgrade("maintenance");
      else toast.error(err?.response?.data?.detail || "Couldn't link to that repair case.");
    } finally { setBusy(false); }
  };

  const createIssue = async () => {
    if (!isPremium) { openUpgrade("maintenance"); return; }
    const ids = linkGuard();
    if (!ids) return;
    setBusy(true);
    try {
      // Create the issue referencing the EXACT uploaded photo ids (no re-upload, no duplicate record).
      const form = new FormData();
      form.append("photo_ids", ids.join(","));
      const { data } = await api.post(`/properties/${propertyId}/maintenance`, form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Maintenance issue created — review the details when you're ready.");
      void data;
      resetSession();
      onOpenChange(false);
      onLinked?.();
    } catch (err) {
      if (err?.response?.status === 402) openUpgrade("maintenance");
      else toast.error(err?.response?.data?.detail || "Couldn't create a maintenance issue.");
    } finally { setBusy(false); }
  };

  const goVideo = () => {
    if (!roomId) return;
    onOpenChange(false);
    navigate(`/capture/${propertyId}/${roomId}`);
  };

  const back = () => {
    if (step === "capture") setStep(roomId && !initialRoomId && rooms.length > 1 ? "room" : "preset");
    else if (step === "room") setStep("preset");
    else if (step === "link") setStep("capture");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-[#0F1B2D] text-white border-white/10" data-testid="urgent-capture-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-white">
            {step !== "preset" && (
              <button onClick={back} data-testid="urgent-back-btn" aria-label="Back"
                className="grid place-items-center h-7 w-7 rounded-full bg-white/10 hover:bg-white/20 transition-colors">
                <ChevronLeft size={16} />
              </button>
            )}
            <Siren size={18} className="text-amber" /> Urgent condition capture
          </DialogTitle>
          <DialogDescription className="text-white/60">
            Document what you can see right now. TenantSeal doesn't diagnose problems or give legal advice.
          </DialogDescription>
        </DialogHeader>

        {readOnly ? (
          <div className="rounded-xl border border-white/10 bg-white/5 p-4 text-sm text-white/70" data-testid="urgent-readonly">
            This property's records are locked or archived, so new photos can't be added here.
          </div>
        ) : (
          <>
            {/* STEP 1 — preset */}
            {step === "preset" && (
              <div className="grid grid-cols-2 gap-2" data-testid="urgent-preset-grid">
                {URGENT_PRESETS.map((p) => (
                  <button key={p.key} data-testid={`urgent-preset-${p.key}`} onClick={() => choosePreset(p.key)}
                    className="rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 active:scale-[0.98] transition-all p-3 text-left text-sm font-medium">
                    {p.label}
                  </button>
                ))}
              </div>
            )}

            {/* STEP 2 — room selection */}
            {step === "room" && (
              <div className="space-y-2" data-testid="urgent-room-picker">
                <p className="text-sm text-white/70">Which room is this in?</p>
                {rooms.length === 0 && (
                  <p className="text-sm text-white/50" data-testid="urgent-no-rooms">Add a room from the capture screen first.</p>
                )}
                {rooms.map((r) => (
                  <button key={r.room_id} data-testid={`urgent-room-${r.room_id}`} onClick={() => chooseRoom(r.room_id)}
                    className="w-full flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 transition-colors p-3 text-left text-sm">
                    <DoorOpen size={16} className="text-amber shrink-0" /> {r.name}
                  </button>
                ))}
              </div>
            )}

            {/* STEP 3 — capture against the shot plan */}
            {step === "capture" && preset && (
              <div className="space-y-3" data-testid="urgent-capture-step">
                <input ref={fileRef} type="file" accept="image/*" capture="environment" multiple onChange={onFiles} className="hidden" data-testid="urgent-file-input" />
                <div className="flex items-center justify-between text-xs text-white/60">
                  <button
                    data-testid="urgent-change-room-btn"
                    onClick={() => rooms.length > 1 && setStep("room")}
                    disabled={rooms.length <= 1}
                    className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 ${rooms.length > 1 ? "bg-white/10 hover:bg-white/20 transition-colors" : ""}`}
                  >
                    <DoorOpen size={13} className="text-amber" /> {roomObj?.name || "Room"}
                    {rooms.length > 1 && <span className="text-white/40">· change</span>}
                  </button>
                  <span className="rounded-full bg-white/10 px-2 py-0.5" data-testid="urgent-preset-chip">{preset.label}</span>
                </div>

                <div className="rounded-xl border border-white/10 bg-white/5 p-3" data-testid="urgent-shot-plan">
                  <p className="text-[11px] font-bold uppercase tracking-wide text-white/40">Suggested shots</p>
                  <ul className="mt-1.5 space-y-1.5">
                    {preset.shots.map((s, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-white/80">
                        <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-amber shrink-0" /> {s}
                      </li>
                    ))}
                  </ul>
                </div>

                {captured.length > 0 && (
                  <div data-testid="urgent-captured-grid">
                    <p className="text-xs text-white/50 mb-1.5">{captured.length} photo{captured.length > 1 ? "s" : ""} saved to this room</p>
                    <div className="flex flex-wrap gap-2">
                      {captured.map((c) => (
                        <div key={c.upload_id} data-testid={`urgent-thumb-${c.upload_id}`}
                          className="h-14 w-14 rounded-lg overflow-hidden border border-white/10 bg-white/5 grid place-items-center">
                          {(c.storage_path || c.thumb)
                            ? <img src={c.storage_path ? fileUrl(c.storage_path, 400) : c.thumb} alt="" className="h-full w-full object-cover" />
                            : <ImagePlus size={16} className="text-white/40" />}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <Button data-testid="urgent-take-photo-btn" disabled={busy} onClick={() => fileRef.current?.click()}
                  className="w-full h-11 rounded-xl bg-amber hover:bg-amber-hover text-navy font-semibold">
                  {busy ? <Loader2 size={16} className="mr-1.5 animate-spin" /> : <Camera size={16} className="mr-1.5" />}
                  {captured.length ? "Take another photo" : "Take a photo"}
                </Button>
                <button onClick={goVideo} data-testid="urgent-video-link"
                  className="w-full text-center text-xs text-white/55 hover:text-white/80 inline-flex items-center justify-center gap-1.5">
                  <Video size={13} /> Prefer a video? Use the room walkthrough recorder
                </button>

                {captured.length > 0 && (
                  <div className="pt-1 space-y-2 border-t border-white/10" data-testid="urgent-choices">
                    <p className="text-xs text-white/50 pt-2">When you're done, choose what to do with this evidence:</p>
                    <Button variant="outline" data-testid="urgent-keep-unlinked-btn" onClick={keepUnlinked}
                      className="w-full h-11 rounded-xl border-white/20 bg-white/5 hover:bg-white/10 text-white justify-start">
                      <Check size={16} className="mr-2 text-emerald-300" /> Keep evidence unlinked
                    </Button>
                    <Button variant="outline" data-testid="urgent-link-existing-btn" disabled={loadingIssues} onClick={startLinkExisting}
                      className="w-full h-11 rounded-xl border-white/20 bg-white/5 hover:bg-white/10 text-white justify-start">
                      {loadingIssues ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Link2 size={16} className="mr-2 text-sky-300" />}
                      Link to an existing repair case
                    </Button>
                    <Button variant="outline" data-testid="urgent-create-issue-btn" disabled={busy} onClick={createIssue}
                      className="w-full h-11 rounded-xl border-white/20 bg-white/5 hover:bg-white/10 text-white justify-start">
                      <Wrench size={16} className="mr-2 text-amber" /> Create a maintenance issue
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* STEP 4 — pick an existing repair case */}
            {step === "link" && (
              <div className="space-y-2" data-testid="urgent-link-picker">
                <p className="text-sm text-white/70">Link your {successfulIds().length} uploaded photo{successfulIds().length === 1 ? "" : "s"} to which repair case?</p>
                {(issues || []).length === 0 && (
                  <p className="text-sm text-white/50" data-testid="urgent-no-issues">No repair cases yet — create a maintenance issue instead.</p>
                )}
                {(issues || []).map((it) => (
                  <button key={it.issue_id} data-testid={`urgent-issue-${it.issue_id}`} disabled={busy} onClick={() => linkToIssue(it)}
                    className="w-full flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 transition-colors p-3 text-left text-sm disabled:opacity-60">
                    <ImagePlus size={16} className="text-sky-300 shrink-0" />
                    <span className="min-w-0 flex-1 truncate">{it.title}</span>
                  </button>
                ))}
              </div>
            )}
          </>
        )}

        <DialogFooter className="sm:justify-start">
          <button onClick={() => onOpenChange(false)} data-testid="urgent-close-btn"
            className="text-xs text-white/50 hover:text-white/80 inline-flex items-center gap-1">
            <X size={13} /> Close
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
